// built-from: /benefit-frame-surface v1 impl:benefit-frame-surface role:implementation
// The benefit frame (056-vision-capture-method, B2 — spec §6 output oracle
// "frame lanes driver→objective→benefit with completeness pips and
// provenance-marked baselines"; render/structure check: an objective card
// ALWAYS shows its driver link or the "no driver yet" open-issue mark; a
// benefit card ALWAYS shows its four completeness pips; mockup STAGE TWO).
// Every engagement gets its frame — this section is always present.
//
//   - LEGACY 054 vision.goal facts render here as driver-less objectives
//     (approved addition #1: a client-side projection, no migration);
//   - a benefit missing its owner renders Confirm disabled with the
//     what-is-missing tooltip (spec check 5 — the server refuses anyway;
//     surfaced honestly). Everything else about the card continues normally;
//   - the baseline pip carries its provenance grade as TEXT plus a
//     grade-named class (spec check 4 — never colour alone);
//   - composers: Add a driver / Add an objective / Add a benefit (the
//     benefit composer needs an objective; with several, a select labelled
//     "Benefit objective" chooses the target — recognition over recall).

import React, { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import type { GatedFact, SurfaceEdit, SurfaceFocus } from '../../facilitation/surfaceBinding';
import { Composer, FactCard, textOf, type EmitResult } from './factCardKit';
import VisionPanel from './VisionPanel';

function contentString(fact: GatedFact, key: string): string {
  const value = fact.content[key];
  return typeof value === 'string' ? value : '';
}

// ── Completeness pips — the four fields, visible whether present or not ─────

const Pip: React.FC<{ label: string; value: string; extraClass?: string }> = ({
  label,
  value,
  extraClass,
}) => {
  const done = value.trim().length > 0;
  const className = [
    'vsf-pip',
    done ? 'vsf-pip--done' : 'vsf-pip--miss',
    done && extraClass ? extraClass : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={className}>
      {label}: {done ? value : '—'}
    </span>
  );
};

function benefitPips(benefit: GatedFact): React.ReactNode {
  const baseline = benefit.content.baseline as
    | { value?: unknown; provenance?: unknown }
    | undefined;
  const baselineValue =
    baseline && typeof baseline.value === 'string' ? baseline.value : '';
  const baselineGrade =
    baseline && typeof baseline.provenance === 'string' ? baseline.provenance : '';
  const baselineText =
    baselineValue && baselineGrade ? `${baselineValue} · ${baselineGrade}` : '';
  return (
    <div className="vsf-pips">
      <Pip label="stakeholder" value={contentString(benefit, 'stakeholder')} />
      <Pip label="owner" value={contentString(benefit, 'owner')} />
      <Pip label="measure" value={contentString(benefit, 'measure')} />
      <Pip
        label="baseline"
        value={baselineText}
        extraClass={baselineGrade ? `vsf-prov--${baselineGrade}` : undefined}
      />
    </div>
  );
}

// ── The benefit composer: one text field, an objective target ───────────────

const BenefitComposer: React.FC<{
  objectives: GatedFact[];
  disabled: boolean;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
}> = ({ objectives, disabled, onEmitEdit }) => {
  const [target, setTarget] = useState('');
  const valid = objectives.some((o) => o.factId === target)
    ? target
    : objectives[0]?.factId ?? '';
  if (!valid) return null;
  return (
    <div className="vsf-benefit-composer">
      {objectives.length > 1 && (
        <select
          className="vsf-benefit-target"
          aria-label="Benefit objective"
          value={valid}
          onChange={(e) => setTarget(e.target.value)}
          disabled={disabled}
        >
          {objectives.map((o) => (
            <option key={o.factId} value={o.factId}>
              {textOf(o) || o.factId}
            </option>
          ))}
        </select>
      )}
      <Composer
        label="Add a benefit"
        placeholder="e.g. Finance regains 8 person-days/month"
        disabled={disabled}
        onCommit={(text) =>
          onEmitEdit({
            capabilityId: 'vision.add_benefit',
            input: { objectiveFactId: valid, text },
          })
        }
      />
    </div>
  );
};

// A benefit is COMPLETE when all four fields stand (the head pill counts
// these; the pips carry the per-card detail).
function isComplete(benefit: GatedFact): boolean {
  const baseline = benefit.content.baseline as { value?: unknown } | undefined;
  const baselineValue =
    baseline && typeof baseline.value === 'string' ? baseline.value : '';
  return (
    contentString(benefit, 'stakeholder').trim().length > 0
    && contentString(benefit, 'owner').trim().length > 0
    && contentString(benefit, 'measure').trim().length > 0
    && baselineValue.trim().length > 0
  );
}

export interface BenefitFrameProps {
  drivers: GatedFact[];
  /** Legacy layout fallback; controller-driven Vision keeps drivers in context. */
  showDrivers?: boolean;
  /** vision.objective facts PLUS legacy vision.goal facts, in log order. */
  objectives: GatedFact[];
  benefits: GatedFact[];
  sealed: boolean;
  busy: boolean;
  lastTurn: Set<string>;
  /** Derived by the surface (owner-approved redesign, 2026-07-09): expanded
   * whenever Benefits is the active act or the frame holds facts. */
  defaultExpanded: boolean;
  /** The active act carries the head tint. */
  active: boolean;
  registerRef: (factId: string, el: HTMLElement | null) => void;
  onEmitEdit: (edit: SurfaceEdit) => EmitResult;
  onPublishFocus?: (focus: SurfaceFocus) => void;
}

const BenefitFrame: React.FC<BenefitFrameProps> = ({
  drivers,
  showDrivers = true,
  objectives,
  benefits,
  sealed,
  busy,
  lastTurn,
  defaultExpanded,
  active,
  registerRef,
  onEmitEdit,
  onPublishFocus,
}) => {
  const driverTextById = new Map(drivers.map((d) => [d.factId, textOf(d)]));
  const hasFacts = (showDrivers ? drivers.length : 0) + objectives.length + benefits.length > 0;
  const completeCount = benefits.filter(isComplete).length;
  const countPill =
    benefits.length > 0 ? `${completeCount} of ${benefits.length} complete` : undefined;

  const cardCommons = {
    sealed,
    frozen: busy,
    registerRef,
    onEmitEdit,
    onPublishFocus,
  };

  return (
    <VisionPanel
      panelId="vsf-frame"
      testId="vsf-frame"
      title="Benefits"
      subLine="What improves, for whom, and how we will know."
      countPill={countPill}
      sealed={sealed}
      active={active}
      defaultExpanded={defaultExpanded}
      strip={{
        label: 'Benefits',
        done: false,
        summary: hasFacts ? countPill ?? 'in progress' : 'nothing captured yet',
      }}
    >
      {showDrivers && <div className="vsf-lane">
        <h3 className="vsf-lane-head">
          Drivers <span className="vsf-link">why change, why now</span>
        </h3>
        <div className="vsf-lane-body">
          {drivers.map((driver) => (
            <FactCard
              key={driver.factId}
              fact={driver}
              noun="driver"
              pulsing={lastTurn.has(driver.factId)}
              {...cardCommons}
            />
          ))}
          {!sealed && (
            <Composer
              label="Add a driver"
              placeholder="e.g. Close consumes finance capacity every month-end"
              disabled={busy}
              onCommit={(text) =>
                onEmitEdit({ capabilityId: 'vision.add_driver', input: { text } })
              }
            />
          )}
        </div>
      </div>}

      <div className="vsf-lane">
        <h3 className="vsf-lane-head">
          Objectives <span className="vsf-link">traceable to a driver</span>
        </h3>
        <div className="vsf-lane-body">
          {objectives.map((objective) => {
            const driverFactId = contentString(objective, 'driverFactId');
            const driverText = driverFactId ? driverTextById.get(driverFactId) : undefined;
            return (
              <FactCard
                key={objective.factId}
                fact={objective}
                noun="objective"
                pulsing={lastTurn.has(objective.factId)}
                detail={
                  driverText ? (
                    <span className="vsf-link">→ driver: {driverText}</span>
                  ) : (
                    // The open-issue mark: visible, never blocking; the
                    // agent probes it in conversation (coherence projection).
                    <span className="vsf-link vsf-link--orphan">
                      <AlertTriangle size={11} aria-hidden="true" /> no driver yet · open
                      issue
                    </span>
                  )
                }
                {...cardCommons}
              />
            );
          })}
          {!sealed && (
            <Composer
              label="Add an objective"
              placeholder="e.g. Close in 3 days with one accountable owner"
              disabled={busy}
              onCommit={(text) =>
                onEmitEdit({ capabilityId: 'vision.add_objective', input: { text } })
              }
            />
          )}
        </div>
      </div>

      <div className="vsf-lane">
        <h3 className="vsf-lane-head">
          Benefits <span className="vsf-link">stakeholder · owner · measure · baseline</span>
        </h3>
        <div className="vsf-lane-body">
          {benefits.map((benefit) => {
            const ownerless = contentString(benefit, 'owner').trim().length === 0;
            return (
              <FactCard
                key={benefit.factId}
                fact={benefit}
                noun="benefit"
                pulsing={lastTurn.has(benefit.factId)}
                confirmBlockedReason={
                  ownerless ? 'Needs an owner before it can be confirmed' : null
                }
                detail={benefitPips(benefit)}
                {...cardCommons}
              />
            );
          })}
          {!sealed && objectives.length > 0 && (
            <BenefitComposer
              objectives={objectives}
              disabled={busy}
              onEmitEdit={onEmitEdit}
            />
          )}
        </div>
      </div>
    </VisionPanel>
  );
};

export default BenefitFrame;
